import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { MenuInfo } from '@/types/menu'

export const usePermissionStore = defineStore('permission', () => {
  // 存储当前路由的权限按钮列表
  const currentPagePermissions = ref<string[]>([])

  // 存储所有菜单数据
  const menuList = ref<MenuInfo[]>([])

  // 存储当前页面按钮信息
  const currentPageButtons = ref<MenuInfo[]>([])

  const isMenuType = (type?: number) => Number(type) === 1
  const isButtonType = (type?: number) => Number(type) === 2

  // 设置当前页面的权限
  const setCurrentPagePermissions = (routePath: string) => {
    const normalizePath = (path: string) => path.replace(/\/+/g, '/').replace(/\/$/, '')
    const targetPath = normalizePath(routePath)

    const buildFullPath = (menu: MenuInfo, parentPath: string): string => {
      const path = menu.routePath.startsWith('/')
        ? menu.routePath
        : `${parentPath}/${menu.routePath}`
      return normalizePath(path)
    }

    const collectButtons = (menu: MenuInfo): MenuInfo[] =>
      (menu.children ?? []).filter(child => isButtonType(child.type))

    // 查找当前路由对应菜单，并记录路径上的父级菜单
    const findTargetMenuWithAncestors = (
      menus: MenuInfo[],
      parentPath: string = '',
      ancestors: MenuInfo[] = [],
    ): { targetMenu: MenuInfo | null; ancestors: MenuInfo[] } => {
      for (const menu of menus) {
        if (!isMenuType(menu.type)) continue

        const currentPath = buildFullPath(menu, parentPath)
        const nextAncestors = [...ancestors, menu]

        if (currentPath === targetPath) {
          return { targetMenu: menu, ancestors }
        }

        if (menu.children?.length) {
          const found = findTargetMenuWithAncestors(menu.children, currentPath, nextAncestors)
          if (found.targetMenu) return found
        }
      }
      return { targetMenu: null, ancestors: [] }
    }

    const { targetMenu, ancestors } = findTargetMenuWithAncestors(menuList.value)

    // 合并当前页面及其父级菜单下的按钮（工作台 Tab 权限通常挂在父级）
    const menusToScan = targetMenu ? [...ancestors, targetMenu] : ancestors
    const buttonMap = new Map<string, MenuInfo>()

    for (const menu of menusToScan) {
      for (const btn of collectButtons(menu)) {
        if (btn.permission) {
          buttonMap.set(btn.permission, btn)
        }
      }
    }

    const buttons = Array.from(buttonMap.values())
    currentPageButtons.value = buttons
    currentPagePermissions.value = buttons
      .filter(btn => btn.status === 1)
      .map(btn => btn.permission)
  }

  // 检查是否有某个权限
  const hasPermission = (permission: string): boolean => {
    return currentPagePermissions.value.includes(permission)
  }

  // 获取按钮状态
  const getButtonState = (permission: string): number => {
    const button = currentPageButtons.value.find(btn => btn.permission === permission)
    return button?.status ?? 0
  }

  // 设置菜单数据
  const setMenuList = (menus: MenuInfo[]) => {
    menuList.value = menus
  }

  return {
    currentPagePermissions,
    menuList,
    currentPageButtons,
    setCurrentPagePermissions,
    hasPermission,
    getButtonState,
    setMenuList
  }
})
